import React from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";

import {
  BUSINESS,
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  DEFAULT_TITLE,
  absoluteUrl,
} from "./siteConfig";
import {
  breadcrumbSchema,
  faqSchema,
  graph,
  localBusinessSchema,
  dentistSchema,
  webPageSchema,
  webSiteSchema,
} from "./schema";

/**
 * Per-route head management: title, description, canonical, Open Graph,
 * Twitter cards and JSON-LD.
 *
 * Every page renders the clinic + dentist + website nodes so that any single
 * page an AI crawler lands on carries the full business context. Page-specific
 * nodes are passed through `schemas`.
 *
 * @param {string}   title        Page title. Rendered verbatim — keep under ~60 chars.
 * @param {string}   description  Meta description. Keep under ~155 chars.
 * @param {string}   [path]       Route path for the canonical URL. Defaults to the current route.
 * @param {string}   [image]      Absolute URL of the social share image.
 * @param {boolean}  [noindex]    Set for pages that must stay out of the index.
 * @param {Array}    [breadcrumbs] [{ name, path }] — first entry should be Home.
 * @param {Array}    [faqs]       [{ q, a }] — must match Q&A visible on the page.
 * @param {Array}    [schemas]    Extra JSON-LD nodes to merge into the graph.
 * @param {string}   [type]       Open Graph type. Defaults to "website".
 */
const Seo = ({
  title,
  description,
  path,
  image = DEFAULT_OG_IMAGE,
  noindex = false,
  breadcrumbs,
  faqs,
  schemas = [],
  type = "website",
  children,
}) => {
  const location = useLocation();
  const resolvedPath = path || location.pathname;
  const url = absoluteUrl(resolvedPath);
  const pageTitle = title || DEFAULT_TITLE;
  const pageDescription = description || DEFAULT_DESCRIPTION;

  const hasPageNode = schemas.some(
    (node) => node && String(node["@id"] || "").endsWith("#webpage")
  );

  const jsonLd = graph([
    localBusinessSchema(),
    dentistSchema(),
    webSiteSchema(),
    hasPageNode
      ? null
      : webPageSchema({ url, title: pageTitle, description: pageDescription, image }),
    breadcrumbSchema(breadcrumbs, url),
    faqSchema(faqs, url),
    ...schemas,
  ]);

  return (
    <Helmet>
      <html lang="en-IN" />
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <link rel="canonical" href={url} />
      <meta
        name="robots"
        content={
          noindex
            ? "noindex, nofollow"
            : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        }
      />
      <meta name="author" content="Dr. Aishwarya Sarda" />

      {/* Local / geo signals */}
      <meta name="geo.region" content="IN-RJ" />
      <meta name="geo.placename" content={`${BUSINESS.address.locality}, ${BUSINESS.city}`} />
      <meta
        name="geo.position"
        content={`${BUSINESS.geo.latitude};${BUSINESS.geo.longitude}`}
      />
      <meta
        name="ICBM"
        content={`${BUSINESS.geo.latitude}, ${BUSINESS.geo.longitude}`}
      />

      {/* Open Graph */}
      <meta property="og:site_name" content={BUSINESS.name} />
      <meta property="og:type" content={type} />
      <meta property="og:locale" content="en_IN" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={pageTitle} />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={image} />

      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      {children}
    </Helmet>
  );
};

export default Seo;
